// ** Redux Imports
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { CheckDomainResult, EntriConfig } from 'entrijs';
import { isNil, set } from 'lodash-es';

import { CustomizerSessionState } from '@/components/modules/deploy/types';
import { CONFIGURATOR_LIVE_STEP, CONFIGURATOR_PAYMENT_STEP } from '@/components/modules/spark/configurator/constants';
import { Campaign, ConfiguratorSessionState } from '@/components/modules/spark/configurator/types';
import { updateUser } from '@/store/slices/authentication';
import { resolveMintCurrency } from '@/utils/region-currency';
import { isDeployBlockedUnpaid } from '@/utils/deploy-gate';
import { hasPaidInvoiceForSession, isAttributableInvoiceRead } from '@/utils/session-invoice';
import {
  acknowledgePayment,
  hasAcknowledged,
  isSessionStateRejection,
  needsPaymentAcknowledgement,
  readAcknowledgement,
  rememberAcknowledgement,
  type PaymentAcknowledgement,
} from '@/utils/session-payment';
import { getErrorCode, getErrorMessage } from '@/utils/errors';
import { readStartMarketHandoff } from '@/utils/start-properties-market';

import { RootState } from '../';

const DEBUG = false;

/** `return-entri-config` soft-failure code: the requested domain is already linked
 *  to a different casino instance, so it can't be connected to this one. The
 *  customizer connect flow keys its "used by another instance" copy off this. */
export const DOMAIN_ALREADY_USED_CODE = 'ERR_1003';

/**
 * Cosmetic deploy-progress labels for the "Done so far" checklist on the Go-live
 * screen (nova customizer deploy step). The backend only streams a `tasks_completed`
 * counter over the websocket (no human label, and the count can even repeat), so we
 * stamp our own believable pipeline messages, advancing one per appended step and
 * cycling once we run past the end. Stored as English source text; the nova `<Text>`
 * renderer translates each at display time, so the strings are hand-maintained keys
 * in `src/locales/<lng>/translation.json` (extractor can't see the dynamic `text=`).
 */
const DEPLOY_STEP_MESSAGES = [
  'Provisioning your cloud infrastructure',
  'Registering your domain and DNS records',
  'Issuing SSL security certificates',
  'Spinning up your game servers',
  'Setting up the player database',
  'Connecting your payment providers',
  'Loading the game catalog',
  'Integrating your game studios',
  'Building the live casino lobby',
  'Applying your brand theme and colors',
  'Optimizing assets for the global CDN',
  'Compiling the responsive front-end',
  'Wiring up the bonus and rewards engine',
  'Configuring KYC and compliance checks',
  'Enabling fraud and risk monitoring',
  'Setting up analytics and reporting',
  'Localizing currencies and languages',
  'Hardening security and access controls',
  'Warming up the CDN edge nodes',
  'Running final health checks',
];

/** Final checklist line stamped when the deploy completes (status → done). */
const DEPLOY_FINAL_MESSAGE = 'Publishing your website to the world';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

type SessionNewProps = undefined;

type SessionNewResult = {
  data: {
    id: string;
  };
  success: boolean;
};

const apiUrl = window.config.sessionApiUrl;

/**
 * @deprecated
 * No longer used, kept for reference
 */
export const sessionNew = createAsyncThunk<SessionNewResult, SessionNewProps, GenericError>(
  'session/new',
  async (_args, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/new`);

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't create new session",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

/**
 * The session's free advertising-credit grant (the "$100 free advertising credit"
 * surfaced on the configurator Players step, `CreditCard`). Returned at the TOP LEVEL
 * of the `/session/load` + `/save` (+ `/check`) envelope, a sibling of `data` /
 * `status` / `campaigns`, NOT inside `data.configuration_data`. `amount` is the
 * headline figure the card shows and is ALWAYS in USD ("$100 USD"); `details` breaks
 * the daily budget across the acquisition channels (web/app/crypto/video) with
 * per-channel `_managed` flags (the same channel shape as the live-brand acquisition
 * budget, but this is the pre-launch promotional credit tied to the session).
 * Named for its domain (advertising credit) to avoid colliding with the live-brand
 * `AcquisitionBudget` in `store/slices/acquisitionBudget.ts`.
 */
export interface SessionAdvertisingCredit {
  /** Total credit (the figure shown on the card). Always USD. */
  amount: number;
  /** Daily spend cap across all channels (USD). */
  daily_amount: number;
  details: {
    web: number;
    web_managed: boolean;
    app: number;
    app_managed: boolean;
    crypto: number;
    crypto_managed: boolean;
    video: number;
    video_managed: boolean;
  };
  /** e.g. `ACTIVE`. */
  status: string;
}

/**
 * The session's applied voucher, echoed at the TOP LEVEL of the `/session/load` + `/save`
 * (+ `/check`) envelope, a sibling of `data` / `status` / `campaigns` (NOT inside
 * `data.configuration_data`, where only the raw `voucher_code` string is persisted). The
 * backend re-evaluates it against the session's bound campaign on every write.
 *
 * `covered_amount` is the discount the voucher applies to the FIRST payment (e.g. it can
 * fully cover the one-time setup fee). It comes back as a whole-currency figure, sometimes
 * typed as a STRING (`"39"`) and sometimes a number (`0`), so consumers must coerce it.
 * `code` is `null` and `covered_amount` `0` when no valid voucher is applied; `error` names
 * a redemption problem (e.g. `VOUCHER_ALREADY_REDEEMED`).
 */
export interface SessionVoucher {
  /** The applied voucher code, or `null` when none/invalid. */
  code: string | null;
  /** Amount the voucher covers off the first payment (whole currency; may arrive as a string). */
  covered_amount: number | string;
  /** ISO currency of `covered_amount` (matches the session currency, e.g. `USD`). */
  currency: string;
  /** Redemption error code when the voucher couldn't be applied (e.g. `VOUCHER_ALREADY_REDEEMED`). */
  error?: string | null;
}

type SessionSaveProps = {
  id: string;
  data: any;
};

type SessionSaveResult = {
  data: {
    id: string;
    campaigns: Campaign[];
    data: any;
    status: SessionStatus;
    /** Free advertising-credit grant, echoed alongside the saved session (see
     *  {@link SessionAdvertisingCredit}). Optional (omitted for sessions with no grant). */
    acquisition_budget?: SessionAdvertisingCredit | null;
    /** Applied voucher, echoed alongside the session (see {@link SessionVoucher}). Optional. */
    voucher?: SessionVoucher | null;
  };
  success: boolean;
};

export const sessionSave = createAsyncThunk<SessionSaveResult, SessionSaveProps, GenericError>(
  'session/save',
  async ({ id, data }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      }

      // Associate the session with the email being saved. The buyer's email lives in the
      // session body (`configuration_data.emailAddress` — written by the configurator and
      // the checkout field); the authenticated user's email is only a fallback. Sourcing
      // it solely from `authentication.user.email` was fragile: that field is in-memory
      // only (wiped on reload / token-less boot), so every save — and with it the
      // server-side email association that `/invoice` requires — silently rejected and the
      // buyer's email never reached the session.
      const savedEmail = data?.configuration_data?.emailAddress;
      const emailAddress = (typeof savedEmail === 'string' && savedEmail.trim()) || state.authentication.user?.email;

      if (isNil(emailAddress) || emailAddress === '') {
        return rejectWithValue({
          error: 'No email address found for current user',
        });
      }

      const response = await axios.post(
        `${apiUrl}/save?email_address=${encodeURIComponent(emailAddress)}`,
        {
          session_id: id,
          data: data,
        },
        {
          headers,
        },
      );

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't save current session",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type SessionLoadProps =
  | {
      id?: string | null;
      email?: string | null;
      token?: string | null;
      /**
       * Explicitly allow MINTING a brand-new session when there is no id to resume.
       * Off by default so ambient/diagnostic loads never create sessions — only the
       * `sessionEnsure` funnel (register / sign-in / configurator entry) passes this.
       */
      create?: boolean;
      /**
       * A marketing-site voucher code to seed onto a FRESH mint (sent as a top-level `/load`
       * body param, which the backend persists into `configuration_data.voucher_code` and
       * evaluates). Only forwarded when actually minting (`create` + no id); a resume never
       * re-sends it (the resumed session already carries it in `configuration_data`).
       */
      voucherCode?: string | null;
      /**
       * Non-owning load: restore the persisted session ONLY if it is a live, resumable
       * build. Set by the reflect-only reads (home "Spotlight" banner, brand switcher,
       * root redirect) AND the magic-link sign-in (whose embedded `session_id` is often a
       * stale, already-advanced build). The {@link sessionLoad} fulfilled reducer then
       * DROPS the id (incl. localStorage) instead of adopting it when the session loaded
       * back {@link NON_RESUMABLE_SESSION_STATUSES} (terminal/live, or stuck
       * `payment_successful`), or — for a no-id reflect read — when `clearSessionData`
       * tore it down mid-flight (the "New brand" race). Deliberate resumes that must carry
       * a paid build forward (the post-payment customizer, the configurator's own
       * `sessionEnsure` on a 3DS/redirect return, the v2 deploy hook) leave this OFF.
       */
      ambient?: boolean;
    }
  | undefined;

type SessionLoadResult = {
  data: any;
  success: boolean;
  /** The session had no id to resume and was found through `GET /session/check` (see {@link sessionLoad}). */
  foundViaCheck?: boolean;
  /** The id this browser kept belongs to another owner (see {@link sessionLoad}): drop it from the store. */
  forgetStoredId?: boolean;
};

/** `POST /load` refused the posted session because it belongs to another owner (backend OHError, HTTP 500). */
const isOwnerMismatch = (error: unknown): boolean =>
  (error as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code === 'SESSION_OWNER_MISMATCH';

/**
 * Whether a no-id load takes the build `GET /session/check` answered with: only one past checkout (the onboarding
 * statuses). A `configuration` session stays with the configurator's own `sessionEnsure`, and a background
 * (`ambient`) read never takes a build it would drop as non-resumable anyway. Pure.
 */
export const resumesFromCheck = (status: SessionStatus | null | undefined, ambient: boolean): boolean =>
  !isNil(status) && ONBOARDING_STATUSES.has(status) && !(ambient && NON_RESUMABLE_SESSION_STATUSES.has(status));

/** One Joi `object.unknown` violation as returned in the /load 400 body's `error[]`. */
type JoiUnknownDetail = { type?: string; path?: string[]; context?: { key?: string } };

/**
 * The single Joi `object.unknown` key from a /load 400 that we're ALLOWED to strip (i.e. it is
 * one of the optional mint hand-off fields), else `null`. The `/load` body is validated with
 * `unknown:false`, so a field the deployed backend doesn't know yet (`or`/`cid` until it adds
 * them) 400s the whole request naming that key; we drop exactly that key and retry.
 */
function unknownMintKeyToStrip(error: unknown, strippable: Set<string>): string | null {
  if (strippable.size === 0) return null;
  const err = error as AxiosError<{ error?: JoiUnknownDetail[] }>;
  if (err?.response?.status !== 400) return null;
  const details = err.response?.data?.error;
  if (!Array.isArray(details)) return null;
  for (const d of details) {
    const key = d?.context?.key ?? d?.path?.[0];
    if (d?.type === 'object.unknown' && typeof key === 'string' && strippable.has(key)) return key;
  }
  return null;
}

/**
 * POST `/session/load`, self-healing over the optional mint hand-off fields (`cpid`/`or`/`cid`)
 * the currently-deployed backend may not accept yet: on a 400 that rejects one of those keys as
 * unknown, drop it and retry, so a fresh mint never breaks on a not-yet-deployed field and
 * automatically carries it once the backend adds it. Any other error propagates unchanged.
 * Bounded by the finite `strippable` set (one strip per unknown key).
 */
async function postLoad(
  data: Record<string, string>,
  headers: Record<string, string>,
  strippable: Set<string>,
): Promise<AxiosResponse<SessionStatusResponse>> {
  const body = { ...data };
  for (;;) {
    try {
      return await axios.post<SessionStatusResponse>(`${apiUrl}/load`, body, { headers });
    } catch (error) {
      const key = unknownMintKeyToStrip(error, strippable);
      if (key == null) throw error;
      delete body[key];
      strippable.delete(key);
    }
  }
}

export const sessionLoad = createAsyncThunk<SessionLoadResult, SessionLoadProps, GenericError>(
  'session/load',
  async (_args, { rejectWithValue, getState, dispatch }) => {
    try {
      const { id, email, token, create, voucherCode } = _args ?? {};

      const state: RootState = getState() as RootState;

      const authToken = token ?? state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      }

      // `create: true` with no explicit id means "MINT a fresh session". It must NOT
      // fall back to the lingering store sessionId: a completed/paid id (e.g. `done`)
      // still sitting in state would otherwise be POSTed to /load and the backend would
      // RELOAD that dead session instead of minting — reopening an already-paid build
      // (old design pre-selected, checkout 404'ing as expired), the exact regression
      // sessionEnsure's status guard exists to prevent.
      let sessionId = id ?? (create === true ? null : state.session.sessionId);
      // The id came from this browser's store (persisted in localStorage), not from the caller.
      const fromStore = isNil(id) && !isNil(sessionId);
      // Who was signed in when this load started (see the identity check after the request).
      const startedAs = state.authentication.user?.user_id ?? null;

      // No id to resume, yet a signed-in owner's paid, mid-setup build is still findable. A support agent's
      // impersonation sign-in carries no session id (nor does an owner on a new device), but its token IS the
      // owner's, and `GET /session/check` answers with the owner's own unfinished build from the token alone
      // (it never mints). Without this, onboarding bounced to `/` and the Finish setup step was unreachable.
      // Signed-in owners only: `/check` requires a user, and a guest token's 401 must not start an auth teardown.
      // A sign-in (password, SSO, register) runs this inside its own thunk, before the store records the owner,
      // and hands over the owner's `token`: that counts as signed in, and `/check` is asked with it, not with
      // whatever identity the store still holds (a guest, or the previous owner on an impersonation switch).
      let foundViaCheck = false;
      const signedIn = !isNil(token) || state.authentication.auth_type === 'user';
      const findViaCheck = async (): Promise<string | null> => {
        if (create === true || !signedIn || isNil(authToken)) return null;
        try {
          const probed = (await dispatch(sessionCheck({ token: authToken })).unwrap()).session;
          if (!isNil(probed) && resumesFromCheck(probed.status as SessionStatus, _args?.ambient === true)) {
            foundViaCheck = true;
            return probed.id;
          }
        } catch {
          // `/check` unreachable: settle as no session, exactly as before.
        }
        return null;
      };
      if (isNil(sessionId)) {
        sessionId = await findViaCheck();
      }

      // Never MINT on an ambient/diagnostic load. Hitting POST /load WITHOUT a
      // session_id makes the backend create a fresh `configuration` session — which
      // is how empty sessions used to pile up on every boot / home / route mount.
      // A session is created ONLY through `sessionEnsure` (the register / sign-in /
      // configurator funnel), which passes `create: true`. Without a known id and
      // without that explicit intent, resolve to a benign "no session" — no request.
      if (isNil(sessionId) && create !== true) {
        return { data: null, success: false };
      }

      const emailAddress = email ?? state.authentication.user?.email;

      const data: Record<string, string> = {};

      if (!isNil(sessionId)) {
        data['session_id'] = sessionId;
      }

      if (!isNil(emailAddress)) {
        data['email_address'] = emailAddress;
      }

      // On a FRESH mint (no id to resume, explicit `create`) forward the marketing-site hand-off
      // so the backend attributes the new customer (`cpid`/`cid`, GUIDs). The `/load` schema
      // accepts `cpid`/`cid`/`currency` (all optional); it does NOT take `or` (the operating region
      // is a separate, FE-managed choice). `postLoad` strips whichever key a not-yet-updated
      // backend still rejects as unknown and retries, so the mint never breaks. Only sent when
      // actually minting, so a resume never re-attributes.
      const strippableMintKeys = new Set<string>();
      if (isNil(sessionId) && create === true) {
        const market = readStartMarketHandoff();
        if (market.cpid) {
          data['cpid'] = market.cpid;
          strippableMintKeys.add('cpid');
        }
        if (market.cid) {
          data['cid'] = market.cid;
          strippableMintKeys.add('cid');
        }
        // Pricing currency comes from the marketing-site MARKET hand-off, not from a configurator
        // flow: the currency selector was removed, so the whole session (bound + recommended
        // campaigns) is priced in whatever it mints in. Precedence (see `@/utils/region-currency`):
        // an explicit hand-off `currency` -> the `/configurator` catalogue's currency for the
        // hand-off's operating region -> `'USD'`. The catalogue is provably NOT loaded yet at mint
        // time (this mint races ahead of the catalogue fetch), so `resolveMintCurrency` falls back
        // to a static region->currency map when the catalogue is null. It MUST be decided here:
        // `/set-currency` only accepts catalogue-active currencies, so a session minted in the
        // wrong one cannot be corrected afterwards. Resume-wins is structural: this block only runs
        // on a fresh mint (`isNil(sessionId) && create`), so a resume never re-sends a currency and
        // the backend keeps whatever the session was already minted in. Sent as a strippable key so
        // a backend that still rejects `currency` retries without it.
        data['currency'] = resolveMintCurrency((getState() as RootState).configuratorConfig.config);
        strippableMintKeys.add('currency');
        // Seed a marketing-site voucher code onto the fresh session. The backend persists it
        // into `configuration_data.voucher_code` and evaluates it, echoing the applied voucher
        // at the envelope top level. Strippable so a backend that doesn't accept the key yet
        // still mints (the voucher is then carried via `configuration_data` on the first save).
        if (voucherCode) {
          data['voucher_code'] = voucherCode;
          strippableMintKeys.add('voucher_code');
        }
      }

      let response: AxiosResponse<SessionStatusResponse>;
      try {
        response = await postLoad(data, headers, strippableMintKeys);
      } catch (error) {
        // The id this browser kept belongs to another owner: a support agent impersonating owner after owner
        // keeps the previous one's build id, and the backend refuses it for the new token. Impersonation links
        // are one-time, so this must never fail the sign-in: forget that id and find the signed-in owner's own
        // build through `/check`. Only for a kept id; an id the caller asked for by name still fails.
        if (!fromStore || foundViaCheck || !isOwnerMismatch(error)) throw error;
        const ownId = await findViaCheck();
        if (isNil(ownId)) return { data: null, success: false, forgetStoredId: true };
        response = await postLoad({ ...data, session_id: ownId }, headers, strippableMintKeys);
      }

      // The owner signed out, or another owner signed in, while this was in flight: the build belongs to who
      // was signed in before, so it must not land in the session that follows. A refreshed token keeps the
      // same owner, and an owner who only became known meanwhile (the boot check) is not a change.
      const nowAs = (getState() as RootState).authentication.user?.user_id ?? null;
      if (!isNil(startedAs) && nowAs !== startedAs) {
        return { data: null, success: false };
      }

      if (!isNil(response.data)) {
        const { email } = state.authentication.user ?? {};
        const { emailAddress } = response.data.data.configuration_data ?? {};

        if (isNil(email) && !isNil(emailAddress)) {
          dispatch(updateUser({ email: emailAddress }));
        }

        return { data: response.data, success: true, foundViaCheck };
      }

      return rejectWithValue({
        error: "Couldn't load current session",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type SessionBindProps = {
  id: string;
};

type SessionBindResult = {
  data: any;
  success: boolean;
};

/**
 * @deprecated
 * No longer used, kept for reference
 */
export const sessionBind = createAsyncThunk<SessionBindResult, SessionBindProps, GenericError>(
  'session/bind',
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      } else {
        return rejectWithValue({
          error: 'No authentication token found',
        });
      }

      const response = await axios.post(
        `${apiUrl}/bind`,
        {
          session_id: id,
        },
        {
          headers,
        },
      );

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't bind current session",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type SessionDeployProps = {
  id: string;
};

type SessionDeployResult = {
  data: any;
  success: boolean;
};

export const sessionDeploy = createAsyncThunk<SessionDeployResult, SessionDeployProps, GenericError>(
  'session/deploy',
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      } else {
        return rejectWithValue({
          error: 'No authentication token found',
        });
      }

      const response = await axios.post(
        `${apiUrl}/deploy`,
        {
          session_id: id,
        },
        {
          headers,
        },
      );

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't deploy current session",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

/* ---------- Stale-session check / cancel ----------

Two endpoints under the same `${sessionApiUrl}` base (window.config.sessionApiUrl =
`<host>/api/owner-hub/v1/session`) let us surface a half-finished, unpaid
configurator session the owner left behind, and let them discard it:

  GET  {sessionApiUrl}/check
    Authorization: <access_token>            (raw token, like every session call)
    → 200 with the owner's in-progress session, e.g.
        {
          "id": "dd79a7d4-…",
          "data": { "configuration_data": { …items, siteSeedId, lastStep, … } },
          "status": "configuration",         // unpaid / mid-build
          "cancellable": true,               // whether /cancel is offered
          "status_details": null,
          "campaigns": [ … ],
          "created_at": "…", "updated_at": "…"
        }
    → empty body / 404 when nothing is pending.

    Unlike a bare `/load` (which MINTS a fresh `configuration` session every call,
    so it can't be used to *detect* a pre-existing one), `/check` is idempotent: it
    reports the owner's existing in-progress session without creating anything. That
    is why the stale-session banner + brand switcher read THIS, not `sessionLoad`.

  POST {sessionApiUrl}/cancel
    Authorization: <access_token>
    body: { "session_id": "<id>" }
    → cancels (discards) the session. We then drop it from `stale` so the banner
      hides, and clear the active session if it was the one cancelled.

The result lives in its own `stale` sub-state so checking never disturbs the active
configurator session (`sessionId`/`status`/`data`).
*/

export interface StaleSession {
  id: string;
  status: SessionStatus;
  data: {
    configuration_data?: ConfiguratorSessionState;
    customization_data?: CustomizerSessionState;
  };
  /** Whether the backend allows this session to be cancelled (gates the Cancel button). */
  cancellable: boolean;
  campaigns?: Campaign[];
  /** Absolute ISO 8601 timestamp the session was started. */
  created_at?: string;
  updated_at?: string;
}

type SessionCheckResult = { session: StaleSession | null };

/**
 * GET {sessionApiUrl}/check — fetch the owner's in-progress configurator session
 * WITHOUT minting one. Resolves to `{ session: null }` when nothing is pending (or
 * on a 404), so a "nothing here" outcome is a normal fulfilled state, not an error.
 */
/** How long to wait for the invoice-status read before treating it as unknown. Generous enough
 *  for a slow connection, short enough that a stalled request can't hold a checkout closed. */
const INVOICE_STATUS_TIMEOUT_MS = 15000;

/** `{ invoiceId, invoiceStatus }` read off the session's current invoice, or nulls.
 *  `coveredByPaidInvoice` is set when the ATTACHED invoice is not paid but a DIFFERENT owner-scoped
 *  invoice for this session IS `paid`; the reducer then adopts `paid` regardless of attribution. */
export type SessionInvoiceStatusResult = {
  invoiceId: string | null;
  invoiceStatus: string | null;
  coveredByPaidInvoice?: boolean;
};

export { hasPaidInvoiceForSession };

/**
 * Read the session's current invoice — `GET {sessionApiUrl}/invoice?session_id=…`.
 *
 * The POST counterpart CREATES (and supersedes), so this is the only safe way to ask "has it
 * been paid?". Needed because the websocket `invoice_update` only fires while a page is open:
 * an owner who closes the tab and returns has no live signal, and onboarding must still know
 * whether the money arrived before it lets them deploy.
 *
 * Resolves to nulls rather than rejecting when there is no invoice or the read fails, so
 * callers can treat "unknown" distinctly from "unpaid" — see `selectDeployBlockedUnpaid`.
 */
export const sessionFetchInvoiceStatus = createAsyncThunk<
  SessionInvoiceStatusResult,
  { sessionId?: string; force?: boolean } | void,
  GenericError
>('session/fetchInvoiceStatus', async (args, { getState }) => {
  const state: RootState = getState() as RootState;
  const sessionId = (args && 'sessionId' in args ? args.sessionId : null) ?? state.session.sessionId;
  if (isNil(sessionId)) return { invoiceId: null, invoiceStatus: null };

  const authToken = state.authentication.access_token;
  const headers: Record<string, string> = {};
  if (!isNil(authToken)) headers['Authorization'] = authToken;

  let attached: SessionInvoiceStatusResult = { invoiceId: null, invoiceStatus: null };
  try {
    // A TIMEOUT is load-bearing here, not defensive dressing: the configurator checkouts hold
    // their payment pane closed until this read settles (opening it would start a payment against
    // an invoice that may already be paid), and there is no global axios timeout in this app — so
    // a hung request would keep that pane shut forever. Failing is fine; hanging is not.
    const response = await axios.get(`${apiUrl}/invoice`, {
      params: { session_id: sessionId },
      headers,
      timeout: INVOICE_STATUS_TIMEOUT_MS,
    });
    const data = response.data;
    if (data && typeof data === 'object' && typeof data.id === 'string') {
      // Reported VERBATIM, including which invoice it is about. Whether that invoice is the one
      // the session is tracking is deliberately NOT decided here: `state` above is a snapshot
      // taken before this ~15s request, and on the path that matters — the deploy step, which
      // fires this on mount while `/session/load` is still in flight — the snapshot's `invoiceId`
      // is still null. Judging attribution against it accepted the superseded row and reproduced
      // the bug on a page reload. The reducer sees LIVE state; it decides.
      attached = { invoiceId: data.id, invoiceStatus: typeof data.status === 'string' ? data.status : null };
    }
  } catch {
    /* No invoice, or the read failed — reported as "unknown" below, never as "unpaid". */
  }

  // `GET /invoice` only ever answers with the session's attached / first invoice, so a session that
  // POINTS at a cancelled / superseded invoice while a DIFFERENT invoice actually paid it reads as
  // unpaid — e.g. a voucher that covered the whole bill minted a `paid`, price-0 invoice after an
  // earlier one was cancelled, or `reconcileSessionInvoice` cancelled a stale invoice and reissued.
  // When the attached read is POSITIVELY not paid (the exact predicate onboarding blocks the launch
  // on), ask the owner-scoped invoice list whether ANY `paid` invoice covers THIS session before we
  // let the stale one block it. A hit is unambiguous — owner (from the token) + session_id + status
  // are all pinned — so it is safe to treat the session as paid; a miss / failed lookup leaves the
  // attached status untouched and never invents a payment.
  if (isDeployBlockedUnpaid(attached.invoiceStatus) && !isNil(authToken)) {
    try {
      const listResponse = await axios.post(
        `${window.config.userApiUrl}/invoice/list`,
        {
          limit: 1,
          offset: 0,
          filter: [
            { name: 'session_id', op: 'eq', value: sessionId },
            { name: 'status', op: 'eq', value: 'paid' },
          ],
        },
        { headers, timeout: INVOICE_STATUS_TIMEOUT_MS },
      );
      if (hasPaidInvoiceForSession(listResponse.data?.data, sessionId)) {
        return { ...attached, coveredByPaidInvoice: true };
      }
    } catch {
      /* Fallback lookup failed — fall through with the attached status; NEVER treat as paid. */
    }
  }
  return attached;
}, {
  /**
   * One read per session, enforced against the LIVE store.
   *
   * The configurator flows mount their entry hook twice (root + checkout step); both re-render in
   * the same commit and React drains every passive effect of that commit before processing the
   * `pending` update the first one caused, so a render-time check in the consumer cannot stop the
   * second dispatch. Aborting here does — and because `condition` prevents `pending` too, the
   * "settled" flag is never armed by a request that was never made.
   */
  condition: (args, { getState }) => {
    const { session } = getState() as RootState;
    // Never two reads in flight, whatever the caller asks for.
    if (session.invoiceStatusLoading) return false;
    // `force` is the escape hatch for a consumer that needs a CURRENT answer rather than "has
    // anyone asked yet". The settled flag is a per-session-lifetime latch, so a probe fired at
    // checkout entry — before the invoice even existed — otherwise silences every later reader
    // for the rest of the page. Onboarding's launch gate is exactly that later reader: it must be
    // able to ask again, or it decides "unknown" and fails open on a brand nobody has paid for.
    if (args && 'force' in args && args.force) return true;
    return !session.invoiceStatusSettled;
  },
});

/**
 * Acknowledge the session's payment — the transition that moves a PAID session out of
 * `configuration` (`POST {sessionApiUrl}/payment-acknowledged`, see
 * [`@/utils/session-payment`](../../utils/session-payment.ts)).
 *
 * A thunk rather than a bare call because the outcome is SHARED STATE: the flow root decides
 * where to send the owner from it, the confirmation screen decides its copy from it, and both
 * mount independently. Holding the verdict here is what keeps them from disagreeing, and it makes
 * the "exactly once" guard a single reducer flag instead of a module global per consumer.
 *
 * Never rejects. The buyer has already paid, so a failed follow-up call must not trap them: the
 * verdict is simply `null` and the confirmation screen falls back to its own signals. An
 * `INVALID_SESSION_STATE` rejection is not even a failure — it means the session had already been
 * advanced (another tab, an earlier visit).
 *
 * Re-reads the session afterwards so `status` reflects the transition. A stale `configuration`
 * would let an autosave fire a `/session/save` the backend now rejects.
 */
export const sessionAcknowledgePayment = createAsyncThunk<
  { verdict: PaymentAcknowledgement | null },
  { sessionId?: string } | void,
  GenericError
>('session/acknowledgePayment', async (args, { getState, dispatch }) => {
  const state: RootState = getState() as RootState;
  const sessionId = (args && 'sessionId' in args ? args.sessionId : null) ?? state.session.sessionId;
  if (isNil(sessionId)) return { verdict: null };

  let verdict: PaymentAcknowledgement | null = null;
  try {
    verdict = await acknowledgePayment(sessionId);
  } catch (error) {
    if (!isSessionStateRejection(error)) {
      DEBUG && console.log('sessionAcknowledgePayment failed', error);
    }
  }
  // Latch BEFORE the re-read, and regardless of the outcome: a failed call may still have
  // transitioned the session (or sent the login-link mail), so a retry on the next mount is the
  // behaviour to avoid. See `rememberAcknowledgement`.
  rememberAcknowledgement(sessionId, verdict);
  // NEVER `ambient: true`: that branch drops a non-resumable session's id (and its localStorage
  // entry), which would break "Continue to Owner Studio" on the confirmation screen.
  await dispatch(sessionLoad({ id: sessionId }));
  return { verdict };
}, {
  /**
   * The real "exactly once" guard, and it has to live HERE.
   *
   * `condition` is evaluated against the LIVE store at dispatch time. A consumer's own check
   * cannot be, because it reads the flag while RENDERING and acts on it in an EFFECT: a flow
   * mounts `useResumePaidSession` twice (root + checkout step), both re-render in the same commit
   * when the invoice status flips to `paid`, and React drains every passive effect of that commit
   * before processing the `pending` update the first one caused. So both effects fire with the
   * same stale "not yet acknowledged" snapshot.
   *
   * Two POSTs for one session is not merely wasteful — the endpoint is not idempotent, so the
   * loser gets `INVALID_SESSION_STATE` and its (successful, error-swallowing) result would latch a
   * NULL verdict over the real one, in the store and in `localStorage`. Aborting here means no
   * second request, no `pending`, and no `fulfilled` to overwrite anything.
   */
  condition: (_args, { getState }) => {
    const { session } = getState() as RootState;
    return !session.paymentAcknowledgeAttempted && !session.paymentAcknowledging;
  },
});

/** `token`: ask as this owner instead of the store's — a sign-in runs before the store records its token. */
type SessionCheckProps = { token?: string } | void;

export const sessionCheck = createAsyncThunk<SessionCheckResult, SessionCheckProps, GenericError>(
  'session/check',
  async (_args, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = _args?.token ?? state.authentication.access_token;

      if (isNil(authToken)) {
        return rejectWithValue({ error: 'No authentication token found' });
      }

      const response = await axios.get(`${apiUrl}/check`, {
        headers: { Authorization: authToken },
      });

      const data = response.data;
      if (data && typeof data === 'object' && typeof data.id === 'string') {
        return {
          session: {
            id: data.id,
            status: data.status,
            data: data.data ?? {},
            cancellable: data.cancellable === true,
            campaigns: data.campaigns,
            created_at: data.created_at,
            updated_at: data.updated_at,
          },
        };
      }

      // 200 with an empty/null body → no pending session.
      return { session: null };
    } catch (error: any) {
      // No in-progress session is a normal outcome, not a failure to surface.
      if (error?.response?.status === 404) {
        return { session: null };
      }
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type SessionCancelProps = { session_id: string };
type SessionCancelResult = { session_id: string; data: any };

/**
 * POST {sessionApiUrl}/cancel — discard a configurator session by id. On success
 * the reducer drops it from `stale` (and clears the active session if it matches),
 * so the stale-session banner hides itself.
 */
export const sessionCancel = createAsyncThunk<SessionCancelResult, SessionCancelProps, GenericError>(
  'session/cancel',
  async ({ session_id }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token;

      if (isNil(authToken)) {
        return rejectWithValue({ error: 'No authentication token found' });
      }

      const response = await axios.post(
        `${apiUrl}/cancel`,
        { session_id },
        { headers: { Authorization: authToken } },
      );

      return { session_id, data: response.data };
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type SessionEnsureResult = { id: string | null };

type SessionEnsureProps =
  | {
      /**
       * Email to stamp onto a FRESH mint (branch 3). Threaded into {@link sessionLoad}'s
       * `/load` body so a newly minted session carries `email_address` from its first write
       * instead of being created email-less and associated a beat later. Resume/adopt
       * (branches 1 and 2) ignore it, since the session already exists. Passed by the
       * register / sign-in funnel, which now mints on submit (once an email is known) rather
       * than on mount, so bounced visits never leave email-less rows.
       */
      email?: string | null;
      /**
       * A marketing-site voucher code to seed onto a FRESH mint (branch 3). Forwarded to
       * {@link sessionLoad}'s `/load` body; resume/adopt ignore it (the session already carries
       * it in `configuration_data`). See {@link SessionVoucher}.
       */
      voucherCode?: string | null;
      /**
       * Establish a CLEAN session for the typed email: skip resuming any cached/existing session
       * (branches 1 and 2) and mint fresh. Set by the register / sign-in funnel, where the email
       * is the source of truth and a client-cached session id (a leftover in localStorage from a
       * previous user of this browser, or an already-advanced build) must NEVER be reused:
       * resuming a foreign/terminal id there loaded the wrong session and silently killed sign-up.
       * Safe because the backend reconciles to the owner's canonical session by email once they
       * authenticate (magic-link return), so the pre-auth client id is a throwaway.
       */
      fresh?: boolean;
    }
  | undefined;

/**
 * Resume-or-create the configurator session — the ONLY path allowed to MINT one.
 *
 * Used where a session is legitimately wanted: the configurator entry (resume a build, the
 * brand switcher's "new brand", or landing on the route directly) and the register / sign-in
 * funnel (which passes `fresh`, see below). Order of preference:
 *   1. resume a session we've ALREADY loaded and verified this run (status `configuration`);
 *   2. else ask the backend, via the token-derived `/check`, which in-progress session is really
 *      ours and adopt it: this is how we resume across a reload WITHOUT blindly trusting an
 *      unverified localStorage id (which may be stale, terminal, or a previous user's on a
 *      shared browser). `/check` no-ops without a token, so the pre-auth funnel falls through;
 *   3. else mint a fresh one (the single intended mint), stamping the caller's email.
 *
 * `fresh: true` (the register / sign-in funnel) SKIPS 1 and 2 and mints for the typed email;
 * the backend reconciles to the owner's canonical session by email on magic-link return, so a
 * leftover client id must not be reused. Everywhere else (boot routing, home notices, the
 * post-payment customizer) must call `sessionLoad()`, which never mints.
 */
export const sessionEnsure = createAsyncThunk<SessionEnsureResult, SessionEnsureProps, GenericError>(
  'session/ensure',
  async (_args, { dispatch, getState }) => {
    const state: RootState = getState() as RootState;
    const { email, fresh, voucherCode } = _args ?? {};

    const knownId = state.session.sessionId;
    const knownStatus = state.session.status;
    const hasToken = !isNil(state.authentication.access_token);

    // The register / sign-in funnel passes `fresh`: the typed email is the source of truth, so we
    // must NOT reuse a cached client session id (a leftover in localStorage from a previous user
    // of this browser, or an already-advanced build). Skip straight to a clean mint; the backend
    // reconciles to the owner's canonical session by email once they authenticate.
    if (fresh !== true) {
      // 1. Resume a session we've already LOADED and verified this run (status `configuration`):
      //    a confirmed, still-editable build, so trusting it needs no round-trip. A NULL status is
      //    UNVERIFIED (a bare reload restored the id from localStorage but hasn't loaded it, or the
      //    id belongs to someone else): do NOT blindly resume it; defer to the backend below.
      if (!isNil(knownId) && knownStatus === SessionStatus.configuration) {
        await dispatch(sessionLoad({ id: knownId })).unwrap();
        return { id: knownId };
      }

      // 2. Ask the backend which session is really ours. `/session/check` is token-derived, so it
      //    returns the OWNER'S genuine in-progress `configuration` session (or nothing), the
      //    reliable way to resume across a reload instead of trusting an unverified localStorage
      //    id. Guard the status explicitly: a build PAST configuration adopted here would later be
      //    POSTed to `/invoice` and rejected with SESSION_INVALID_STATE. `/check` needs a token,
      //    so it no-ops on the pre-auth register/login page and we fall through to mint.
      //
      //    Every resume/adopt attempt below gets its OWN try/catch: whichever step fails, control
      //    must fall through to the mint at the end, never reject sessionEnsure outright and never
      //    treat a DIFFERENT step's failure as if `/check` itself were down (each catch's fallback
      //    is scoped to exactly the call it wraps).
      try {
        const probe = await dispatch(sessionCheck()).unwrap();
        const probed = probe.session;
        if (!isNil(probed) && probed.status === SessionStatus.configuration) {
          try {
            await dispatch(sessionLoad({ id: probed.id })).unwrap();
            return { id: probed.id };
          } catch {
            // Adopting the OWNER'S real session failed (not a `/check` failure) — fall through to
            // mint rather than resuming a DIFFERENT, unverified `knownId` via the `/check`-failure
            // fallback below, which exists only for when `/check` itself couldn't be reached.
          }
        }
      } catch {
        // `/check` failed. When AUTHED and holding a reload id that isn't KNOWN terminal, resume it
        // rather than mint a duplicate, so the configurator still resumes across a reload if `/check`
        // is transiently down. A null status is unverified (never loaded this run) and is still worth
        // the optimistic resume; a status we already loaded as NON_RESUMABLE must not be, or a
        // completed/paid build gets reopened and later 500s on /invoice with SESSION_INVALID_STATE.
        // Pre-auth has no token here, so it correctly falls through to mint.
        if (hasToken && !isNil(knownId) && (isNil(knownStatus) || !NON_RESUMABLE_SESSION_STATUSES.has(knownStatus))) {
          try {
            await dispatch(sessionLoad({ id: knownId })).unwrap();
            return { id: knownId };
          } catch {
            // The optimistic resume itself failed (e.g. the cached id is dead server-side) — fall
            // through to mint rather than rejecting sessionEnsure entirely; `/check` being down must
            // never leave the app without a session.
          }
        }
      }
    }

    // 3. Nothing trustworthy to resume/adopt → mint fresh (the single intended mint). Stamp the
    //    caller's email onto the fresh `/load` body (see SessionEnsureProps) so the minted session
    //    carries `email_address` from its first write rather than being created email-less.
    const created = await dispatch(sessionLoad({ create: true, email, voucherCode })).unwrap();
    return { id: created.success ? created.data?.id ?? null : null };
  },
);

export interface ConfigReducer {
  sessionId: string | null;
  campaigns: Campaign[];
  recommendedCampaigns: Campaign[];
  data: {
    configuration_data?: ConfiguratorSessionState;
    customization_data?: CustomizerSessionState;
  };
  status: SessionStatus | null;
  /** The session's free advertising-credit grant (see {@link SessionAdvertisingCredit}),
   *  captured from the TOP LEVEL of the load/save envelope. `null` when the backend
   *  hasn't granted one, in which case the configurator credit card hides its amount. */
  advertisingCredit: SessionAdvertisingCredit | null;
  /** The session's applied voucher (see {@link SessionVoucher}), captured from the TOP LEVEL
   *  of the load/save envelope. `null` when no voucher is applied, in which case the
   *  configurator checkout hides the voucher line. */
  voucher: SessionVoucher | null;
  postAuth: 'mail' | 'redirect' | null;
  postPayment: 'mail' | 'redirect' | 'success-mail' | 'success-redirect' | null;
  /** Id of the session's current invoice, echoed by `/session/load` once one exists. */
  invoiceId: string | null;
  /**
   * Latest invoice status, pushed by the `invoice_update` websocket message.
   *
   * This is the settlement signal for the ASYNCHRONOUS rails: a SPEI transfer clears minutes to
   * a business day after the buyer leaves the checkout, and `'paid'` is when the brand can
   * actually be deployed. `null` until an update arrives.
   */
  invoiceStatus: string | null;
  /**
   * The backend's verdict from `POST /session/payment-acknowledged`: whether the owner can go
   * straight into onboarding, or has to open a login-link email first. `null` means no verdict
   * (never acknowledged from here, or the call failed) — consumers fall back to their own signals
   * rather than guessing a `false`.
   */
  /**
   * The invoice-status read is in flight / has settled for the current session.
   *
   * `invoiceStatus` alone cannot express "we looked and found nothing", and the difference
   * matters: a checkout holds its payment pane closed until the answer is in, so "not yet" and
   * "no invoice" must not look the same. `settled` is true once the read resolves EITHER WAY, so
   * a failed or timed-out read releases the gate instead of wedging it.
   */
  invoiceStatusLoading: boolean;
  invoiceStatusSettled: boolean;
  paymentAcknowledgement: PaymentAcknowledgement | null;
  /** True while the acknowledge transition is in flight, so a screen can hold steady. */
  paymentAcknowledging: boolean;
  /**
   * The acknowledge has been ATTEMPTED for the current session, EVER — including by an earlier
   * page load, rehydrated from the durable latch. This is the "exactly once" guard:
   * `/payment-acknowledged` is not idempotent, and when it answers `can_continue: false` the
   * session STAYS on `configuration`, so the status alone cannot stop a second attempt.
   */
  paymentAcknowledgeAttempted: boolean;
  /**
   * The acknowledge happened on THIS page visit — so this visit is the one that completed the
   * buyer's checkout, and owes them the confirmation screen.
   *
   * Deliberately separate from `paymentAcknowledgeAttempted`, which the latch also sets. Only the
   * narrower flag may drive ROUTING: an owner who paid on an earlier visit, read the confirmation
   * and moved into onboarding must keep being sent to their onboarding, not looped back to a
   * confirmation screen they have already acted on.
   */
  paymentAcknowledgedThisVisit: boolean;
  loaded: boolean;
  loading: boolean;
  error: any;
  /** A half-finished, unpaid configurator session the owner left behind, fetched
   *  by {@link sessionCheck} — kept apart from the active session above so checking
   *  never disturbs it. Drives the home "Spotlight" banner + brand-switcher resume. */
  stale: {
    loaded: boolean;
    loading: boolean;
    cancelling: boolean;
    error: any;
    session: StaleSession | null;
  };
  deploy: {
    loaded: boolean;
    loading: boolean;
    tasksCompleted: number;
    progress: number;
    domain: string;
    steps: Record<string, string>[];
  };
  domainConnect: {
    loaded: boolean;
    loading: boolean;
    config: EntriConfig | null;
    check: CheckDomainResult | null;
    error: any;
    /** Machine-readable code for the last failure (e.g. {@link DOMAIN_ALREADY_USED_CODE}),
     *  so consumers branch on the cause rather than parsing the message string. */
    errorCode: string | null;
  };
}

export enum SessionStatus {
  configuration = 'configuration',
  // A payment is in flight (async confirmation) / failed and awaiting retry. These
  // are surfaced by the home "Important Now" banner; the backend sets them on the
  // session as the Stripe PaymentIntent settles (processing → successful, or → failed).
  payment_processing = 'payment_processing',
  payment_failed = 'payment_failed',
  payment_successful = 'payment_successful',
  customization = 'customization',
  deployment_in_progress = 'deployment_in_progress',
  deployment_complete = 'deployment_complete',
  deployment_failed = 'deployment_failed',
  done = 'done',
  cancelled = 'cancelled',
}

/**
 * Statuses a persisted `sessionId` must NOT be ambiently adopted from (see the
 * `ambient` flag on {@link sessionLoad}). Either the build is terminal/live
 * (`done` / `deployment_complete` / `cancelled` — nothing left to resume) or it is
 * STUCK at `payment_successful`: paid, but the backend advance to `customization`
 * never happened, so the onboarding customizer's first `/session/save` is rejected
 * with `INVALID_SESSION_STATE` (HTTP 500). Surfacing such an orphan is exactly what
 * produced the bogus "Finish setting up the website you purchased…" banner, bounced
 * the configurator back to Home, and 500'd the onboarding save. The actionable
 * states stay adoptable: `configuration` (configurator), the in-flight
 * `payment_processing` / `payment_failed` (banner + retry), and the genuinely
 * resumable `customization` / `deployment_in_progress` / `deployment_failed`.
 */
export const NON_RESUMABLE_SESSION_STATUSES: ReadonlySet<SessionStatus> = new Set([
  SessionStatus.payment_successful,
  SessionStatus.deployment_complete,
  SessionStatus.done,
  SessionStatus.cancelled,
]);

/**
 * Whether the persisted `state.session` can be RESUMED as-is by the configurator bootstrap, versus
 * needing a fresh `sessionEnsure` (adopt-or-mint). Extracted from the four byte-identical configurator
 * store guards so the rule lives in one tested place (mirrors why the status SETS above are shared).
 *
 * Resumable requires ALL of:
 *  - `loaded` — the session has been loaded this run, so we are reading a settled value not a
 *    pre-load default;
 *  - a non-null `sessionId` — there is an actual session to resume. A loaded-but-idless state is NOT
 *    resumable: an SSO sign-in for a new account ambiently no-op-loads the session (no id to bind) yet
 *    still flips `loaded=true`, and without a sessionId the configurator can neither fetch recommended
 *    campaigns nor persist edits, so the wizard ran session-less to a $0 review. This id check is the
 *    fix for that bug (the previous inline guard omitted it and wrongly treated loaded+idless as
 *    resumable, skipping the rescue mint);
 *  - an editable status — unknown yet (`null`, an unverified reload) or `configuration`. A build PAST
 *    configuration (paid / customising / deployed / done) is not resumable here: the wizard would
 *    persist onto it and `/invoice` would reject it with SESSION_INVALID_STATE.
 *
 * CONFIGURATOR-SCOPED, deliberately: "resumable" here means "the configurator wizard may keep editing
 * this build", which is NARROWER than general adoptability. Statuses like `customization` /
 * `deployment_*` / `payment_failed` are legitimately adopted ELSEWHERE (the customizer, `sessionEnsure`'s
 * `/check` fallback — see {@link NON_RESUMABLE_SESSION_STATUSES}); they are simply not resumable by the
 * configurator. Do NOT reuse this as a universal "can I resume this session" check, or a paid
 * `customization` build the customizer legitimately resumes would be wrongly dropped.
 */
export const isConfiguratorSessionResumable = (
  session: Pick<ConfigReducer, 'loaded' | 'sessionId' | 'status'>,
): boolean =>
  session.loaded &&
  session.sessionId != null &&
  (session.status == null || session.status === SessionStatus.configuration);

export interface SessionConfiguration {
  data: any;
  status: SessionStatus.configuration;
}

export interface SessionCustomization {
  data: any;
  status: SessionStatus.customization;
}

export interface SessionDeployProgress {
  data: any;
  status: SessionStatus.deployment_in_progress;
  status_details: {
    tasks_completed: number;
    progress: number;
  };
}

export interface SessionDeployCompleted {
  data: any;
  status: SessionStatus.done;
}

export type SessionStatusResponse =
  | SessionConfiguration
  | SessionCustomization
  | SessionDeployProgress
  | SessionDeployCompleted;

type SessionFetchRecommendedCampaignsProps = {
  id: string;
};

export const sessionFetchRecommendedCampaigns = createAsyncThunk<
  Campaign[],
  SessionFetchRecommendedCampaignsProps,
  GenericError
>('session/fetchRecommendedCampaigns', async ({ id }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const response = await axios.post(`${apiUrl}/recommended-campaigns`, { session_id: id }, { headers });

    if (!isNil(response.data)) {
      // API returns a bare array
      const campaigns: Campaign[] = Array.isArray(response.data) ? response.data : response.data.data ?? [];
      return campaigns;
    }

    return rejectWithValue({ error: "Couldn't fetch recommended campaigns" });
  } catch (error) {
    return rejectWithValue({ error: getErrorMessage(error) });
  }
});

type SessionSetRecommendedCampaignProps = {
  id: string;
  campaignId: string;
};

type SessionSetRecommendedCampaignResult = {
  data: {
    id: string;
    campaigns: Campaign[];
    data: any;
    status: SessionStatus;
    /** Free advertising-credit grant, echoed with the re-selected campaign (see
     *  {@link SessionAdvertisingCredit}). Optional. */
    acquisition_budget?: SessionAdvertisingCredit | null;
    /** Applied voucher, echoed alongside the session (see {@link SessionVoucher}). Optional. */
    voucher?: SessionVoucher | null;
  };
  success: boolean;
};

export const sessionSetRecommendedCampaign = createAsyncThunk<
  SessionSetRecommendedCampaignResult,
  SessionSetRecommendedCampaignProps,
  GenericError
>('session/setRecommendedCampaign', async ({ id, campaignId }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const response = await axios.post(
      `${apiUrl}/set-recommended-campaign`,
      { session_id: id, campaign_id: campaignId },
      { headers },
    );

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({ error: "Couldn't set recommended campaign" });
  } catch (error) {
    return rejectWithValue({ error: getErrorMessage(error) });
  }
});

type SessionSetCurrencyProps = {
  /** ISO 4217 code (e.g. `USD` / `MXN`) — same convention as the `/load` mint's `currency`. */
  currency: string;
};

type SessionSetCurrencyResult = {
  data: {
    id: string;
    campaigns: Campaign[];
    data: any;
    status: SessionStatus;
    /** Free advertising-credit grant, echoed with the re-priced session (see
     *  {@link SessionAdvertisingCredit}). Optional. */
    acquisition_budget?: SessionAdvertisingCredit | null;
    /** Applied voucher, echoed alongside the session (see {@link SessionVoucher}). Optional. */
    voucher?: SessionVoucher | null;
  };
  success: boolean;
};

/**
 * POST `{sessionApiUrl}/set-currency` — change the currency the whole session is priced in.
 * The backend RE-PRICES the session and returns a `/load`-shaped object (`{ id, data, status,
 * campaigns }`) with `data.configuration_data.currency` and the campaign prices in the new
 * currency. The recommended-campaigns list is priced the same way but fetched separately, so
 * consumers should re-fetch it after this settles (the configurator store does).
 *
 * NOTE: the body sends the ISO code (matching the `/load` mint + the documented contract). Some
 * deployments of this endpoint currently parse `currency` as a numeric id and reject the ISO
 * string ("not a valid Int"); this rejects cleanly (leaves the session unchanged) until the
 * backend accepts the ISO code like `/load` does.
 */
export const sessionSetCurrency = createAsyncThunk<SessionSetCurrencyResult, SessionSetCurrencyProps, GenericError>(
  'session/setCurrency',
  async ({ currency }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      } else {
        return rejectWithValue({ error: 'No authentication token found' });
      }

      const sessionId = state.session.sessionId;

      if (isNil(sessionId)) {
        return rejectWithValue({ error: 'No session to set currency on' });
      }

      const response = await axios.post(
        `${apiUrl}/set-currency`,
        { session_id: sessionId, currency },
        { headers },
      );

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({ error: "Couldn't set session currency" });
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

type SessionDomainConnectConfigProps = {
  event_type: string;
  event_data: string;
};

type SessionDomainConnectConfigResult = {
  data: any;
  success: boolean;
};

/** This thunk's rejection carries the server error `code` (e.g. {@link DOMAIN_ALREADY_USED_CODE})
 *  alongside the message, so the connect flow can branch on it. */
type DomainConnectConfigError = {
  rejectValue: {
    error: string;
    code?: string;
  };
};

export const sessionDomainConnectConfig = createAsyncThunk<
  SessionDomainConnectConfigResult,
  SessionDomainConnectConfigProps,
  DomainConnectConfigError
>('session/domain/entriConfig', async ({ event_data, event_type }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const sessionId = state.session.sessionId;

    const response = await axios.post<SessionDomainConnectConfigResult>(
      `${apiUrl}/domains/entri/return-entri-config`,
      {
        session_id: sessionId,
        event_data,
        event_type,
      },
      {
        headers,
      },
    );

    // This endpoint signals a soft failure with an HTTP 200 body of
    // `{ success: false, errorCode, message }` (NOT an HTTP error, NOT the standard
    // `{ error: { code } }` envelope), e.g. ERR_1003 when the domain is already
    // linked to another instance. Reject so it surfaces as `domainConnect.error`
    // (with the code) instead of being mistaken for a successful — but config-less
    // — response that strands the flow on the misleading "manual setup" screen.
    const body = response.data as { success?: boolean; errorCode?: string; message?: string } | null;
    if (body?.success === false) {
      return rejectWithValue({
        error: body.message ?? "Couldn't get Entri config",
        code: body.errorCode,
      });
    }

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't get Entri config",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
      code: getErrorCode(error),
    });
  }
});

type SessionDomainConnectSetupBeginProps = {
  event_type: string;
  event_data: string;
};

type SessionDomainConnectSetupBeginResult = {
  data: any;
  success: boolean;
};

export const sessionDomainConnectSetupBegin = createAsyncThunk<
  SessionDomainConnectSetupBeginResult,
  SessionDomainConnectSetupBeginProps,
  GenericError
>('session/domain/entriSetupBegin', async ({ event_data, event_type }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const sessionId = state.session.sessionId;

    const response = await axios.post<SessionDomainConnectSetupBeginResult>(
      `${apiUrl}/domains/entri/setup-begin`,
      {
        session_id: sessionId,
        event_data,
        event_type,
      },
      {
        headers,
      },
    );

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't notify server about starting configuration",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

type SessionDomainConnectSetupSuccessProps = {
  event_type: string;
  event_data: string;
};

type SessionDomainConnectSetupSuccessResult = {
  data: any;
  success: boolean;
};

export const sessionDomainConnectSetupSuccess = createAsyncThunk<
  SessionDomainConnectSetupSuccessResult,
  SessionDomainConnectSetupSuccessProps,
  GenericError
>('session/domain/entriSetupSuccess', async ({ event_data, event_type }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const sessionId = state.session.sessionId;

    const response = await axios.post<SessionDomainConnectSetupSuccessResult>(
      `${apiUrl}/domains/entri/setup-success`,
      {
        session_id: sessionId,
        event_data,
        event_type,
      },
      {
        headers,
      },
    );

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't notify server about configuration success",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

type SessionDomainConnectSetupFailureProps = {
  event_type: string;
  event_data: string;
};

type SessionDomainConnectSetupFailureResult = {
  data: any;
  success: boolean;
};

export const sessionDomainConnectSetupFailure = createAsyncThunk<
  SessionDomainConnectSetupFailureResult,
  SessionDomainConnectSetupFailureProps,
  GenericError
>('session/domain/entriSetupFailure', async ({ event_data, event_type }, { rejectWithValue, getState }) => {
  try {
    const state: RootState = getState() as RootState;

    const authToken = state.authentication.access_token;

    const headers: Record<string, string> = {};

    if (!isNil(authToken)) {
      headers['Authorization'] = authToken;
    } else {
      return rejectWithValue({
        error: 'No authentication token found',
      });
    }

    const sessionId = state.session.sessionId;

    const response = await axios.post<SessionDomainConnectSetupFailureResult>(
      `${apiUrl}/domains/entri/setup-failed`,
      {
        session_id: sessionId,
        event_type,
        event_data,
      },
      {
        headers,
      },
    );

    if (!isNil(response.data)) {
      return { data: response.data, success: true };
    }

    return rejectWithValue({
      error: "Couldn't notify server about configuration failure",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

/**
 * Drop the stale-session probe once the live session it refers to has advanced past
 * `configuration` (i.e. it was resumed and paid). Keeps the "unpaid" banner from
 * lingering or reappearing after a resume+pay that happens without a full reload —
 * a stronger guarantee than the read-time cross-check in {@link selectStaleUnpaidSession}.
 * `id` defaults to the active `sessionId` for status updates that don't carry one.
 */
const dropStaleIfAdvanced = (
  state: ConfigReducer,
  status: SessionStatus | null | undefined,
  id?: string | null,
): void => {
  const stale = state.stale.session;
  if (!stale || status == null || status === SessionStatus.configuration) return;
  if ((id ?? state.sessionId) === stale.id) state.stale.session = null;
};

/**
 * Whether an invoice-status action belongs to a session that is no longer the active one.
 *
 * The read is asynchronous and the active session can change under it: a boot that adopts a
 * persisted `sessionId` fires the probe, then `sessionEnsure` resolves a DIFFERENT session, and the
 * original read lands afterwards. Applying it then writes one session's invoice onto another —
 * and a stale `paid` is exactly what makes an unpaid session look settled enough to acknowledge.
 *
 * Every caller passes the id it read for, so the check is cheap. A caller that passed no id meant
 * "the current session", which by definition still matches.
 */
const isForeignProbe = (
  state: ConfigReducer,
  action: { meta: { arg: { sessionId?: string } | void } },
): boolean => {
  const probedId = (action.meta.arg && 'sessionId' in action.meta.arg ? action.meta.arg.sessionId : null) ?? null;
  return probedId != null && state.sessionId !== probedId;
};

/**
 * Forget everything known about the session's INVOICE, and the payment verdict derived from it.
 *
 * Must run at every point the active session is dropped. These fields say "money has arrived for
 * the session in this store", so carrying them into the NEXT session is not a cosmetic leak: a
 * stale `invoiceStatus: 'paid'` makes a brand-new, unpaid `configuration` session look settled,
 * which is enough to acknowledge it and hand the owner a free brand. It also short-circuits the
 * fresh session's own invoice probe, so nothing would correct it.
 */
const clearInvoiceState = (state: ConfigReducer): void => {
  state.invoiceId = null;
  state.invoiceStatus = null;
  state.invoiceStatusLoading = false;
  state.invoiceStatusSettled = false;
  state.paymentAcknowledgement = null;
  state.paymentAcknowledging = false;
  state.paymentAcknowledgeAttempted = false;
  state.paymentAcknowledgedThisVisit = false;
};

/** Drop the session a load brought (or the id it was asked with) from the store and localStorage; status stays null. */
const forgetLoadedSession = (state: ConfigReducer): void => {
  state.sessionId = null;
  state.status = null;
  state.campaigns = [];
  state.advertisingCredit = null;
  state.voucher = null;
  // The session is being dropped, so its invoice must go with it — otherwise the
  // next session inherits a `paid` status it never earned (see clearInvoiceState).
  clearInvoiceState(state);
  localStorage.removeItem('sessionId');
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState: <ConfigReducer>{
    sessionId: localStorage.getItem('sessionId') || null,
    campaigns: [],
    recommendedCampaigns: [],
    data: {},
    status: null,
    advertisingCredit: null,
    voucher: null,
    postAuth: null,
    postPayment: null,
    invoiceId: null,
    invoiceStatus: null,
    invoiceStatusLoading: false,
    invoiceStatusSettled: false,
    paymentAcknowledgement: null,
    paymentAcknowledging: false,
    paymentAcknowledgeAttempted: false,
    paymentAcknowledgedThisVisit: false,
    loaded: false,
    loading: false,
    error: null,
    stale: {
      loaded: false,
      loading: false,
      cancelling: false,
      error: null,
      session: null,
    },
    deploy: {
      loaded: false,
      loading: false,
      tasksCompleted: 0,
      progress: 0,
      domain: '',
      steps: [],
    },
    domainConnect: {
      loaded: false,
      loading: false,
      config: null,
      check: null,
      error: null,
      errorCode: null,
    },
  },
  reducers: {
    clearRecommendedCampaigns: (state) => {
      state.recommendedCampaigns = [];
    },
    clearSessionData: (state) => {
      state.sessionId = null;
      // state.data = {};
      state.status = null;
      state.loaded = false;
      state.loading = false;
      state.error = null;
      state.recommendedCampaigns = [];
      // Drop the per-session advertising-credit grant so a new brand's credit card
      // doesn't briefly show the previous session's amount.
      state.advertisingCredit = null;
      // Likewise drop the applied voucher so a new brand's checkout doesn't inherit it.
      state.voucher = null;
      // Drop the invoice, and everything derived from it, or a paid brand LEAKS INTO THE NEXT
      // ONE. This teardown runs on "New brand": leaving `invoiceStatus: 'paid'` behind meant the
      // freshly minted session (status `configuration`) satisfied
      // `needsPaymentAcknowledgement('configuration', 'paid')`, so the entry hook acknowledged an
      // UNPAID session and advanced it to `customization` — a free casino. The leaked truthy
      // status also suppressed the new session's own `GET /session/invoice` probe, so nothing
      // would have corrected it. Same class of leak as the one that forced
      // {@link clearDeployProgress}.
      clearInvoiceState(state);
      // `postAuth` is a ONE-SHOT routing signal (set to 'redirect'/'mail' by
      // authenticationLoginEmail, consumed once by the login/register post-auth
      // effect). It was never reset after consumption, so it lingered in the store
      // the whole time a freshly-signed-in owner sat in the configurator. On logout
      // this teardown wiped the session id/status but LEFT the stale 'redirect',
      // so the login page's effect immediately bounced /login → /configurator-v3,
      // which (now unauthenticated) bounced straight back to /login — an infinite
      // redirect loop ("Maximum update depth exceeded", the flickering page). Clear
      // it here so logout/teardown leaves no stale routing intent behind.
      state.postAuth = null;
      // Drop any stale-session probe too so logout/teardown leaves no trace of the
      // previous owner's half-finished build for the next user (the listener in
      // store/index.ts calls this on logout).
      state.stale = { loaded: false, loading: false, cancelling: false, error: null, session: null };

      localStorage.removeItem('sessionId');
    },
    /** Forget the probed stale session (e.g. after the owner dismisses the banner)
     *  without hitting the API. */
    clearStaleSession: (state) => {
      state.stale.session = null;
    },
    /** Reset the deploy-progress slice to its initial (pre-deploy) state.
     *  `clearSessionData` deliberately leaves `deploy` ALONE so the post-deploy
     *  "your site is live" screen survives the session teardown that fires alongside
     *  the WS `done` event. The cost is that `deploy.loaded` (and steps/progress/
     *  domain) then LEAK into the next brand's onboarding — making its deploy step
     *  render the "done" phase on entry and skip the "Finish setup" CTA, so the new
     *  brand never deploys. The customizer dispatches this once on entry to start
     *  each onboarding from a clean `review` phase. */
    clearDeployProgress: (state) => {
      state.deploy = {
        loaded: false,
        loading: false,
        tasksCompleted: 0,
        progress: 0,
        domain: '',
        steps: [],
      };
    },
    updateSessionPostAuth: (state, action) => {
      state.postAuth = action.payload;
    },
    updateSessionPostPayment: (state, action) => {
      state.postPayment = action.payload.postPayment;
      state.status = action.payload.status;
      dropStaleIfAdvanced(state, action.payload.status);
    },
    /**
     * `invoice_update` websocket message: `{ invoice_id, invoice_status }`.
     *
     * The settlement signal for asynchronous rails. A SPEI transfer clears long after the buyer
     * has left the checkout screen, so this is how a still-open page learns the money arrived
     * (`invoice_status === 'paid'`, at which point the brand can be deployed). Ignores updates
     * for a DIFFERENT invoice than the one this session is tracking, so a superseded invoice's
     * late message can't overwrite the current one's state.
     */
    updateSessionInvoice: (state, action) => {
      const { invoice_id: invoiceId, invoice_status: invoiceStatus } = action.payload ?? {};
      if (state.invoiceId && invoiceId && state.invoiceId !== invoiceId) return;
      if (invoiceId) state.invoiceId = invoiceId;
      if (invoiceStatus) state.invoiceStatus = invoiceStatus;
    },
    /**
     * A checkout MINTED a replacement invoice (gate2way: every Continue mints, the backend
     * supersedes). Unlike `updateSessionInvoice` (adopt-on-null, fed by the websocket) this RE-POINTS
     * the tracked id, so the next `invoice_update` for the new invoice passes the guard above. The
     * status is dropped with the old id (it described the superseded invoice).
     *
     * Deliberately leaves `invoiceStatusSettled` alone (resetting it flips
     * `useResumePaidSession.resolving` and closes the pane) and never touches the acknowledgement
     * fields (a replacement is minted only while unpaid).
     */
    replaceSessionInvoice: (state, action: PayloadAction<{ invoice_id: string }>) => {
      state.invoiceId = action.payload.invoice_id;
      state.invoiceStatus = null;
    },
    setSessionProgress: (state, action) => {
      const payload = action.payload as SessionStatusResponse;

      switch (payload.status) {
        case SessionStatus.deployment_in_progress:
          state.deploy.loading = true;
          state.deploy.loaded = false;
          state.deploy.tasksCompleted = payload.status_details.tasks_completed;
          state.deploy.progress = payload.status_details.progress;
          // Advance one cosmetic label per appended step (cycling past the end), keyed
          // off our own step count rather than the backend counter — which can repeat.
          state.deploy.steps = [
            ...state.deploy.steps,
            { text: DEPLOY_STEP_MESSAGES[state.deploy.steps.length % DEPLOY_STEP_MESSAGES.length] },
          ];
          break;

        case SessionStatus.done:
          state.deploy.loading = false;
          state.deploy.loaded = true;
          state.deploy.tasksCompleted = state.deploy.tasksCompleted + 1;
          state.deploy.progress = 100;
          state.deploy.steps = [...state.deploy.steps, { text: DEPLOY_FINAL_MESSAGE }];
          break;

        default:
          break;
      }

      state.status = payload.status;
      dropStaleIfAdvanced(state, payload.status);
    },
    setDomainCheck: (state, action) => {
      state.domainConnect.check = action.payload;
    },
    clearDomainConnect: (state) => {
      state.domainConnect.loaded = false;
      state.domainConnect.loading = false;
      state.domainConnect.config = null;
      state.domainConnect.check = null;
      state.domainConnect.error = null;
      state.domainConnect.errorCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sessionNew.fulfilled, (state, action) => {
        state.loaded = true;
        state.loading = false;
        if (action.payload.success) {
          state.sessionId = action.payload.data.id;

          localStorage.setItem('sessionId', action.payload.data.id);
        }
        DEBUG && console.timeEnd('sessionNew.query');
        DEBUG && console.log('sessionNew.fulfilled', action.payload);
      })
      .addCase(sessionNew.pending, (state) => {
        state.loaded = false;
        state.loading = true;
        DEBUG && console.time('sessionNew.query');
      })
      .addCase(sessionNew.rejected, (state, action) => {
        state.loaded = false;
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionNew.rejected', action.payload);
      })

      .addCase(sessionSave.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionId = action.payload.data.id;
        state.campaigns = action.payload.data.campaigns;
        const savedIncomingData = action.payload.data.data ?? {};
        state.data = {
          ...savedIncomingData,
          configuration_data: {
            ...state.data.configuration_data,
            ...savedIncomingData.configuration_data,
          },
        };
        state.status = action.payload.data.status;
        // Refresh the advertising-credit grant when the save echoes it. Guarded on the
        // key's presence: `/save` returns only the status-matching half of the session,
        // so an omitted grant must NOT wipe the one captured at load. See {@link SessionAdvertisingCredit}.
        if ('acquisition_budget' in action.payload.data) {
          state.advertisingCredit = action.payload.data.acquisition_budget ?? null;
        }
        // Refresh the applied voucher. A save that EXPLICITLY cleared the code — we sent
        // `voucher_code: ''` from a checkout-review Remove — drops the voucher DETERMINISTICALLY,
        // even if this status-half echo omits the `voucher` key; otherwise the removed discount +
        // chip would linger on screen while the server has already cleared it. Any other save
        // in-guards on the key's presence like the credit grant: `/save` returns only the
        // status-matching half, so an omitted voucher must NOT wipe the one captured at load.
        // See {@link SessionVoucher}.
        const sentVoucherCode: unknown = action.meta.arg?.data?.configuration_data?.voucher_code;
        if (sentVoucherCode === '') {
          state.voucher = null;
        } else if ('voucher' in action.payload.data) {
          state.voucher = action.payload.data.voucher ?? null;
        }
        dropStaleIfAdvanced(state, action.payload.data.status, action.payload.data.id);
        DEBUG && console.timeEnd('sessionSave.query');
        DEBUG && console.log('sessionSave.fulfilled', action.payload);
      })
      .addCase(sessionSave.pending, (state, action) => {
        state.loading = true;
        state.data = action.meta.arg.data ?? {};
        DEBUG && console.time('sessionSave.query');
      })
      .addCase(sessionSave.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionSave.rejected', action.payload);
      })

      .addCase(sessionLoad.fulfilled, (state, action) => {
        // `loaded` marks the load as SETTLED, not "a session exists". A non-minting
        // no-op load (no id to resume, `create` not set → success:false) is a settled
        // outcome with no session: RootRedirect and useImportantNotices gate on
        // `loaded` to stop waiting / re-dispatching, so they MUST see `true` here even
        // when success is false. Do NOT change this to `loaded = action.payload.success`
        // — that would hang the boot loader forever for any user without a persisted
        // session. On success:false we simply leave sessionId/data/status untouched,
        // unless the id this browser kept turned out to be another owner's (forget it).
        state.loaded = true;
        state.loading = false;
        if (!action.payload.success && action.payload.forgetStoredId === true) {
          forgetLoadedSession(state);
        }
        if (action.payload.success) {
          // Non-owning loads (home banner / brand switcher / root redirect, AND the
          // magic-link sign-in — all flagged `ambient: true`) restore the persisted session
          // ONLY if it is a live, resumable build. Skip adoption — and forget the id — when:
          //  (a) it loaded back NON-RESUMABLE ({@link NON_RESUMABLE_SESSION_STATUSES}):
          //      terminal/live, or stuck at `payment_successful` (paid but never advanced to
          //      `customization`, so onboarding's /save 500s INVALID_SESSION_STATE). Adopting
          //      such an orphan is what surfaced the bogus "Finish setting up…" banner, bounced
          //      the configurator back to Home, 500'd the save — and a magic link re-adopted it
          //      on EVERY relogin (its embedded session_id points at the old advanced build); or
          //  (b) for a no-id reflect read, the session was torn down by `clearSessionData`
          //      (the brand switcher's "New brand") WHILE this load was in flight — re-adopting
          //      the now-stale result would RESURRECT it (the "New brand" bounce). Gated on
          //      `isNil(id)` so an explicit-id resume after logout (sessionId already null) is
          //      NOT mistaken for a mid-load teardown.
          // Drop the id (incl. localStorage), leave status null. Deliberate paid-build resumes
          // (customizer, sessionEnsure's 3DS return, v2 deploy) pass no `ambient` flag.
          if (action.meta.arg?.ambient === true) {
            const loadedStatus = action.payload.data.status as SessionStatus;
            // A build found through `/check` had no store id to begin with, so an empty store is not a teardown.
            const clearedMidLoad =
              isNil(action.meta.arg.id) && action.payload.foundViaCheck !== true && state.sessionId === null;
            if (clearedMidLoad || NON_RESUMABLE_SESSION_STATUSES.has(loadedStatus)) {
              forgetLoadedSession(state);
              return;
            }
          }
          // A load that brings a DIFFERENT session (or a different invoice for the same one)
          // invalidates everything we knew about the old invoice. Only `invoiceId` is refreshed
          // from the envelope below; `invoiceStatus` and the payment verdict have no counterpart
          // in it, so without this they would survive the switch and make an unpaid session look
          // settled. Computed BEFORE `sessionId` is overwritten. The same-session reload that
          // `sessionAcknowledgePayment` performs matches on both ids, so the verdict it just
          // obtained is preserved.
          const loadedInvoiceId = (action.payload.data.invoice_id as string | null) ?? null;
          const switchedSession =
            (state.sessionId != null && state.sessionId !== action.payload.data.id) ||
            (state.invoiceId != null && loadedInvoiceId != null && state.invoiceId !== loadedInvoiceId);
          if (switchedSession) clearInvoiceState(state);

          state.sessionId = action.payload.data.id;
          state.campaigns = action.payload.data.campaigns;
          state.data = action.payload.data.data ?? {};
          state.status = action.payload.data.status;

          // Capture the session's free advertising-credit grant from the TOP LEVEL of
          // the envelope (sibling of `data`), driving the configurator credit card. A
          // `/load` is the authoritative full snapshot, so a missing/null grant clears
          // it and the card hides its amount. See {@link SessionAdvertisingCredit}.
          state.advertisingCredit =
            (action.payload.data.acquisition_budget as SessionAdvertisingCredit | null) ?? null;

          // Capture the applied voucher from the TOP LEVEL of the envelope (sibling of
          // `data`), driving the checkout voucher line. A `/load` is the authoritative full
          // snapshot, so a missing/null voucher clears it. See {@link SessionVoucher}.
          state.voucher = (action.payload.data.voucher as SessionVoucher | null) ?? null;

          // Id of the session's current invoice, present once one has been created. It lets a
          // checkout that reloads re-READ its invoice (`GET /session/invoice`) instead of
          // POSTing a fresh one — which matters on the SPEI rail, where re-creating supersedes
          // the invoice and voids bank coordinates the buyer may already be paying against.
          // A `/load` is the authoritative snapshot, so an absent id clears it.
          state.invoiceId = loadedInvoiceId;

          // Re-hydrate the acknowledgement from its durable latch. The store starts empty on
          // every page load, but the transition is NOT repeatable: when it answers
          // `can_continue: false` the session stays on `configuration` with a paid invoice, so
          // without this a reload would acknowledge again and send a second login-link email.
          // It also restores the verdict, so a reloaded confirmation screen still knows whether
          // to offer onboarding or point the owner at their inbox.
          if (!state.paymentAcknowledgeAttempted && hasAcknowledged(action.payload.data.id)) {
            state.paymentAcknowledgeAttempted = true;
            state.paymentAcknowledgement = readAcknowledgement(action.payload.data.id);
          }

          // Return to Review step if the payment was abandoned
          const paymentNotCompleted =
            action.payload.data?.data?.configuration_data?.lastStep === CONFIGURATOR_PAYMENT_STEP.id;
          const stepToRestore = paymentNotCompleted
            ? CONFIGURATOR_LIVE_STEP.id
            : action.payload.data?.data?.configuration_data?.lastStep;
          // Using lodash set in order to avoid TypeScript checks
          if (!isNil(stepToRestore)) {
            set(state.data, 'configuration_data.lastStep', stepToRestore);
          }

          localStorage.setItem('sessionId', action.payload.data.id);

          dropStaleIfAdvanced(state, action.payload.data.status, action.payload.data.id);
        }
        DEBUG && console.timeEnd('sessionLoad.query');
        DEBUG && console.log('sessionLoad.fulfilled', action.payload);
      })
      .addCase(sessionLoad.pending, (state) => {
        state.loaded = false;
        state.loading = true;
        // Clear a prior failure so a stale error can't latch into the retry
        // (mirrors sessionDomainConnectConfig.pending).
        state.error = null;
        DEBUG && console.time('sessionLoad.query');
      })
      .addCase(sessionLoad.rejected, (state, action) => {
        state.loaded = false;
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionLoad.rejected', action.payload);
      })

      .addCase(sessionBind.fulfilled, (state, action) => {
        state.loading = false;
        DEBUG && console.timeEnd('sessionBind.query');
        DEBUG && console.log('sessionBind.fulfilled', action.payload);
      })
      .addCase(sessionBind.pending, (state) => {
        state.loading = true;
        DEBUG && console.time('sessionBind.query');
      })
      .addCase(sessionBind.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionBind.rejected', action.payload);
      })

      .addCase(sessionDeploy.fulfilled, (state, action) => {
        state.deploy.loading = true;
        state.deploy.domain = `https://${action.payload.data?.data?.internal_domain}`;
        DEBUG && console.timeEnd('sessionDeploy.query');
        DEBUG && console.log('sessionDeploy.fulfilled', action.payload);
      })
      .addCase(sessionDeploy.pending, (state) => {
        state.deploy.loading = true;
        DEBUG && console.time('sessionDeploy.query');
      })
      .addCase(sessionDeploy.rejected, (state, action) => {
        state.deploy.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionDeploy.rejected', action.payload);
      })

      .addCase(sessionCheck.pending, (state) => {
        state.stale.loading = true;
        state.stale.error = null;
      })
      .addCase(sessionAcknowledgePayment.pending, (state) => {
        // Flag the attempt on PENDING, not on fulfilled. The thunk is not idempotent, so the
        // guard has to close the instant it is dispatched — a second consumer mounting while the
        // request is still in flight would otherwise fire the transition again.
        state.paymentAcknowledgeAttempted = true;
        state.paymentAcknowledgedThisVisit = true;
        state.paymentAcknowledging = true;
      })
      .addCase(sessionAcknowledgePayment.fulfilled, (state, action) => {
        state.paymentAcknowledging = false;
        state.paymentAcknowledgement = action.payload.verdict;
        DEBUG && console.log('sessionAcknowledgePayment.fulfilled', action.payload);
      })
      .addCase(sessionAcknowledgePayment.rejected, (state, action) => {
        // The thunk swallows its own errors, so this only fires on something unforeseen. Leave
        // the verdict null: the confirmation screen falls back to its own signals rather than
        // trapping a buyer who has already paid.
        state.paymentAcknowledging = false;
        DEBUG && console.log('sessionAcknowledgePayment.rejected', action.payload);
      })
      .addCase(sessionFetchInvoiceStatus.pending, (state, action) => {
        if (isForeignProbe(state, action)) return;
        state.invoiceStatusLoading = true;
      })
      .addCase(sessionFetchInvoiceStatus.rejected, (state, action) => {
        if (isForeignProbe(state, action)) return;
        // The thunk swallows its own errors, so this is a belt-and-braces branch. It still has to
        // mark the read SETTLED: a checkout gated on the answer must be released either way.
        state.invoiceStatusLoading = false;
        state.invoiceStatusSettled = true;
      })
      .addCase(sessionFetchInvoiceStatus.fulfilled, (state, action) => {
        if (isForeignProbe(state, action)) return;
        state.invoiceStatusLoading = false;
        state.invoiceStatusSettled = true;
        // A DIFFERENT invoice PAID this session even though the attached one is cancelled/superseded:
        // the owner-scoped `/invoice/list` fallback confirmed a `paid` row for this session_id. Adopt
        // `paid` directly — this is session-scoped evidence, so it deliberately does NOT go through the
        // attached-invoice attribution below (whose job is to reject a status read off a NON-tracked
        // invoice). It cannot false-positive: the match pins owner + session_id + status.
        if (action.payload.coveredByPaidInvoice) {
          state.invoiceStatus = 'paid';
          return;
        }
        // `GET /session/invoice` answers with the session's FIRST invoice, not its live one, so
        // this read may describe an invoice that was superseded long ago. Attribution is decided
        // HERE rather than in the thunk because only the reducer sees live state — the thunk's
        // snapshot predates its own ~15s request.
        //
        // Unattributable is UNKNOWN, and nothing is adopted from it. `invoiceId` in particular is
        // never written from this read: it decides whether an `invoice_update` push is ours or
        // foreign (see updateSessionInvoice), so pointing it at the wrong invoice makes the live
        // `paid` message get DISCARDED — precisely how a settled card payment still read as unpaid
        // at the deploy step. The session envelope and the websocket own that value; both populate
        // it correctly, and this read has no information either of them lacks.
        if (!isAttributableInvoiceRead(action.payload.invoiceId, state.invoiceId)) return;
        // Never overwrite a LIVE websocket status with a stale read: `invoice_update` is
        // pushed the moment the money lands, and this fetch can resolve after it.
        if (action.payload.invoiceStatus && state.invoiceStatus !== 'paid') {
          state.invoiceStatus = action.payload.invoiceStatus;
        }
      })
      .addCase(sessionCheck.fulfilled, (state, action) => {
        state.stale.loading = false;
        state.stale.loaded = true;
        state.stale.session = action.payload.session;
        DEBUG && console.log('sessionCheck.fulfilled', action.payload);
      })
      .addCase(sessionCheck.rejected, (state, action) => {
        state.stale.loading = false;
        state.stale.loaded = true;
        state.stale.error = action.payload?.error;
        DEBUG && console.log('sessionCheck.rejected', action.payload);
      })

      .addCase(sessionCancel.pending, (state) => {
        state.stale.cancelling = true;
        state.stale.error = null;
      })
      .addCase(sessionCancel.fulfilled, (state, action) => {
        state.stale.cancelling = false;
        // Drop the cancelled session from the stale probe so the banner hides.
        if (state.stale.session?.id === action.payload.session_id) {
          state.stale.session = null;
        }
        // Re-arm the one-shot probe (useImportantNotices / brand switcher gate on
        // `!loaded && !loading`) so a stale session created later this session is
        // still picked up without a full reload.
        state.stale.loaded = false;
        // If it was also the active configurator session, clear it so nothing tries
        // to resume a cancelled build.
        if (state.sessionId === action.payload.session_id) {
          state.sessionId = null;
          state.status = null;
          state.loaded = false;
          // Its invoice is gone with it. A cancelled session's status must not survive to make
          // the next session look paid (see clearInvoiceState).
          clearInvoiceState(state);
          localStorage.removeItem('sessionId');
        }
        DEBUG && console.log('sessionCancel.fulfilled', action.payload);
      })
      .addCase(sessionCancel.rejected, (state, action) => {
        state.stale.cancelling = false;
        state.stale.error = action.payload?.error;
        DEBUG && console.log('sessionCancel.rejected', action.payload);
      })

      .addCase(sessionFetchRecommendedCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendedCampaigns = action.payload;
        DEBUG && console.log('sessionFetchRecommendedCampaigns.fulfilled', action.payload);
      })
      .addCase(sessionFetchRecommendedCampaigns.pending, (state) => {
        state.loading = true;
      })
      .addCase(sessionFetchRecommendedCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionFetchRecommendedCampaigns.rejected', action.payload);
      })

      .addCase(sessionSetRecommendedCampaign.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.sessionId = action.payload.data.id;
          state.campaigns = action.payload.data.campaigns;
          state.status = action.payload.data.status;

          // Refresh the advertising-credit grant if this response echoes it. In-guarded
          // like `/save` (this echoes the status-matching half), so an omitted grant
          // keeps the load-captured value. See {@link SessionAdvertisingCredit}.
          if ('acquisition_budget' in action.payload.data) {
            state.advertisingCredit = action.payload.data.acquisition_budget ?? null;
          }
          // Refresh the applied voucher if this response echoes it (in-guarded like `/save`).
          if ('voucher' in action.payload.data) {
            state.voucher = action.payload.data.voucher ?? null;
          }

          // Merge incoming data so existing configuration_data fields (lastStep, siteSeedId, etc.) survive.
          const incomingData = action.payload.data.data ?? {};
          state.data = {
            ...incomingData,
            configuration_data: {
              ...state.data.configuration_data,
              ...incomingData.configuration_data,
            },
          };

          // Always persist the confirmed campaignId regardless of whether the campaign
          // object can be found in the response or cached lists.
          const campaignId = action.meta.arg.campaignId;
          if (!state.data.configuration_data) state.data.configuration_data = {};
          state.data.configuration_data.campaignId = campaignId;

          // Prefer the response campaign (has server-confirmed price_evaluation) over the
          // cached recommendedCampaigns version (which may have match: false / stale prices).
          const responseCampaign = action.payload.data.campaigns?.find(
            (c: Campaign) => String(c.id) === String(campaignId),
          );
          const selectedCampaign =
            responseCampaign ?? state.recommendedCampaigns.find((c) => String(c.id) === String(campaignId));
          if (selectedCampaign) {
            state.data.configuration_data.items = selectedCampaign.default_items as any;
            // Keep recommendedCampaigns in sync with the authoritative response data.
            if (responseCampaign) {
              const idx = state.recommendedCampaigns.findIndex((c) => String(c.id) === String(campaignId));
              if (idx >= 0) state.recommendedCampaigns[idx] = responseCampaign;
            }
          }
        }
        DEBUG && console.log('sessionSetRecommendedCampaign.fulfilled', action.payload);
      })
      .addCase(sessionSetRecommendedCampaign.pending, (state) => {
        state.loading = true;
      })
      .addCase(sessionSetRecommendedCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionSetRecommendedCampaign.rejected', action.payload);
      })

      .addCase(sessionSetCurrency.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.campaigns = action.payload.data.campaigns;
          state.status = action.payload.data.status;
          // Refresh the advertising-credit grant if this response echoes it (in-guarded,
          // like `/save`). See {@link SessionAdvertisingCredit}.
          if ('acquisition_budget' in action.payload.data) {
            state.advertisingCredit = action.payload.data.acquisition_budget ?? null;
          }
          // Refresh the applied voucher if this response echoes it (in-guarded like `/save`).
          if ('voucher' in action.payload.data) {
            state.voucher = action.payload.data.voucher ?? null;
          }
          // Merge incoming data so existing configuration_data fields (design tokens, items,
          // campaignId, lastStep, …) survive — the response echoes the re-priced session +
          // `configuration_data.currency`, and the reducer keeps the rest of the local state.
          const incomingData = action.payload.data.data ?? {};
          state.data = {
            ...incomingData,
            configuration_data: {
              ...state.data.configuration_data,
              ...incomingData.configuration_data,
            },
          };
        }
        DEBUG && console.log('sessionSetCurrency.fulfilled', action.payload);
      })
      .addCase(sessionSetCurrency.pending, (state) => {
        state.loading = true;
      })
      .addCase(sessionSetCurrency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('sessionSetCurrency.rejected', action.payload);
      })

      .addCase(sessionDomainConnectConfig.fulfilled, (state, action) => {
        state.domainConnect.loading = false;
        state.domainConnect.loaded = true;
        if (action.payload.data.success) {
          state.domainConnect.config = action.payload.data.config;
        }
        DEBUG && console.timeEnd('sessionDomainConnectConfig.query');
        DEBUG && console.log('sessionDomainConnectConfig.fulfilled', action.payload);
      })
      .addCase(sessionDomainConnectConfig.pending, (state) => {
        state.domainConnect.loading = true;
        // Clear any prior failure so a retry starts clean (the consumer settles
        // on domainConnect.error, see below).
        state.domainConnect.error = null;
        state.domainConnect.errorCode = null;
        DEBUG && console.time('sessionDomainConnectConfig.query');
      })
      .addCase(sessionDomainConnectConfig.rejected, (state, action) => {
        state.domainConnect.loading = false;
        // Record the failure on domainConnect (not just the global session error)
        // so consumers that gate on `domainConnect.error` (the customizer connect
        // flow) settle instead of hanging with the submit pinned. The `code` lets
        // the flow tell "domain already used" (ERR_1003) apart from generic errors.
        state.domainConnect.error = action.payload?.error;
        state.domainConnect.errorCode = action.payload?.code ?? null;
        DEBUG && console.log('sessionDomainConnectConfig.rejected', action.payload);
      })

      .addCase(sessionDomainConnectSetupBegin.fulfilled, (state, action) => {
        state.domainConnect.loading = false;
        DEBUG && console.timeEnd('sessionDomainConnectSetupBegin.query');
        DEBUG && console.log('sessionDomainConnectSetupBegin.fulfilled', action.payload);
      })
      .addCase(sessionDomainConnectSetupBegin.pending, (state) => {
        state.domainConnect.loading = true;
        DEBUG && console.time('sessionDomainConnectSetupBegin.query');
      })
      .addCase(sessionDomainConnectSetupBegin.rejected, (state, action) => {
        state.domainConnect.loading = false;
        state.domainConnect.error = action.payload?.error;
        DEBUG && console.log('sessionDomainConnectSetupBegin.rejected', action.payload);
      })

      .addCase(sessionDomainConnectSetupSuccess.fulfilled, (state, action) => {
        state.domainConnect.loading = false;
        DEBUG && console.timeEnd('sessionDomainConnectSetupSuccess.query');
        DEBUG && console.log('sessionDomainConnectSetupSuccess.fulfilled', action.payload);
      })
      .addCase(sessionDomainConnectSetupSuccess.pending, (state) => {
        state.domainConnect.loading = true;
        DEBUG && console.time('sessionDomainConnectSetupSuccess.query');
      })
      .addCase(sessionDomainConnectSetupSuccess.rejected, (state, action) => {
        state.domainConnect.loading = false;
        state.domainConnect.error = action.payload?.error;
        DEBUG && console.log('sessionDomainConnectSetupSuccess.rejected', action.payload);
      })

      .addCase(sessionDomainConnectSetupFailure.fulfilled, (state, action) => {
        state.domainConnect.loading = false;
        DEBUG && console.timeEnd('sessionDomainConnectSetupFailure.query');
        DEBUG && console.log('sessionDomainConnectSetupFailure.fulfilled', action.payload);
      })
      .addCase(sessionDomainConnectSetupFailure.pending, (state) => {
        state.domainConnect.loading = true;
        DEBUG && console.time('sessionDomainConnectSetupFailure.query');
      })
      .addCase(sessionDomainConnectSetupFailure.rejected, (state, action) => {
        state.domainConnect.loading = false;
        state.domainConnect.error = action.payload?.error;
        DEBUG && console.log('sessionDomainConnectSetupFailure.rejected', action.payload);
      });
  },
});

export const {
  clearRecommendedCampaigns,
  clearSessionData,
  clearStaleSession,
  clearDeployProgress,
  setSessionProgress,
  updateSessionPostAuth,
  updateSessionPostPayment,
  updateSessionInvoice,
  replaceSessionInvoice,
  setDomainCheck,
  clearDomainConnect,
} = sessionSlice.actions;
export type { SessionSetRecommendedCampaignResult };

/**
 * The owner's stale, UNPAID configurator session (status `configuration`) if one
 * exists — i.e. a website they started building but never paid for. Returns `null`
 * for a paid/in-setup session (`customization` etc. are handled by the home
 * "casino-setup" notice + the onboarding wizard, not this banner).
 */
/**
 * The free advertising-credit headline amount (always USD) for the configurator credit
 * card, or `null` when no credit has been granted (the card then hides its amount).
 */
export const selectAdvertisingCreditAmount = (state: RootState): number | null =>
  state.session.advertisingCredit?.amount ?? null;

/** The session's applied voucher (see {@link SessionVoucher}), or `null` when none is applied. */
export const selectSessionVoucher = (state: RootState): SessionVoucher | null => state.session.voucher;

/** The amount the applied voucher covers off the first payment, coerced to a number (the backend
 *  sometimes returns it as a string). `0` when no voucher is applied or it covered nothing (an
 *  unknown / already-redeemed code). */
export const selectVoucherCoveredAmount = (state: RootState): number => {
  const raw = state.session.voucher?.covered_amount;
  const n = typeof raw === 'string' ? Number(raw) : raw ?? 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const selectStaleUnpaidSession = (state: RootState): StaleSession | null => {
  const session = state.session.stale.session;
  if (!session || session.status !== SessionStatus.configuration) return null;
  // If the active session IS this one but has since advanced past `configuration`
  // (e.g. the owner just resumed and paid, before the next `/check`), it's no longer
  // an unpaid stale session — hide it rather than waiting for a re-probe.
  if (
    state.session.sessionId === session.id &&
    state.session.status != null &&
    state.session.status !== SessionStatus.configuration
  ) {
    return null;
  }
  return session;
};

/**
 * Session statuses that mean "paid, brand being set up, not yet live" — the whole
 * post-payment, pre-live range (onboarding/customizer → deploy). A session in one of
 * these is an in-progress PAID build: the owner can only set up one website at a time,
 * so it both routes a returning user to onboarding ({@link resolvePostAuthRoute}) and
 * suppresses "New brand" / blocks the configurator. `done`/`deployment_complete` (live)
 * and `configuration` (unpaid, resumable in the configurator) are deliberately excluded.
 */
export const ONBOARDING_STATUSES: ReadonlySet<SessionStatus> = new Set([
  SessionStatus.payment_successful,
  SessionStatus.customization,
  SessionStatus.deployment_in_progress,
  SessionStatus.deployment_failed,
]);

/**
 * True when the active session is a paid, not-yet-live build (see {@link ONBOARDING_STATUSES}).
 * Drives: the brand switcher (replaces "New brand" with "Continue setup" → onboarding) and
 * the configurator-v3 route guard (redirects home — you can't start another brand while one
 * is still being set up).
 */
/**
 * True only when we POSITIVELY know the session's invoice has not been paid — the signal that
 * onboarding must not let the owner deploy yet. Fails open; the rule (and why) lives in
 * [`@/utils/deploy-gate`](../../utils/deploy-gate.ts), kept pure so it can be unit-tested
 * without booting the slice.
 */
export const selectDeployBlockedUnpaid = (state: RootState): boolean =>
  isDeployBlockedUnpaid(state.session.invoiceStatus);

/** The session's invoice is settled. The only settlement evidence on every rail: `customization`
 *  is NOT proof of payment (`/payment-acknowledged` can reach it on the buyer's word). */
export const selectSessionInvoicePaid = (state: RootState): boolean => state.session.invoiceStatus === 'paid';

/**
 * The session has been PAID FOR but never advanced, so the flow must acknowledge it — once.
 *
 * Both halves of {@link needsPaymentAcknowledgement} plus the once-guard, which the status alone
 * cannot provide: a `can_continue: false` answer leaves the session on `configuration`, so
 * re-deriving this from the status would re-fire the transition (and re-send its email) on every
 * remount.
 */
export const selectNeedsPaymentAcknowledgement = (state: RootState): boolean =>
  !state.session.paymentAcknowledgeAttempted &&
  needsPaymentAcknowledgement(state.session.status, state.session.invoiceStatus);

/**
 * This page opened on a session whose checkout is ALREADY DONE, so the owner is returning to a
 * completed purchase rather than starting one. Flows use it to open on the confirmation screen
 * instead of an order summary they can no longer change.
 *
 * Two ways to be true, and the second is why the attempted flag is part of it: the invoice is
 * paid (settled, whatever the status), or we acknowledged this session's payment and the backend
 * has moved it on. The latter keeps the answer stable AFTER the transition — the status is no
 * longer `configuration`, so the paid-and-unadvanced test that started it all has gone false.
 */
/**
 * Statuses that mean the session is PAST its checkout — enumerated, deliberately not "anything
 * but `configuration`".
 *
 * `payment_failed` and `cancelled` are the reason. A clean `stripe.confirmPayment` can resolve
 * with the intent still `processing`, so the acknowledge fires and the flow shows its confirmation
 * before settlement; if that charge then fails, the backend moves the session to `payment_failed`.
 * A "not configuration" test would read that as checked out and keep claiming "Payment
 * successful" for a payment that did not go through. Those two statuses belong back in the wizard,
 * where the retry-payment banner deep-links them.
 */
const CHECKED_OUT_SESSION_STATUSES: ReadonlySet<SessionStatus> = new Set([
  SessionStatus.payment_processing,
  SessionStatus.payment_successful,
  SessionStatus.customization,
  SessionStatus.deployment_in_progress,
  SessionStatus.deployment_failed,
  SessionStatus.deployment_complete,
  SessionStatus.done,
]);

export const selectSessionCheckedOut = (state: RootState): boolean =>
  state.session.invoiceStatus === 'paid' ||
  state.session.status === SessionStatus.payment_successful ||
  (state.session.paymentAcknowledgeAttempted &&
    state.session.status != null &&
    CHECKED_OUT_SESSION_STATUSES.has(state.session.status));

/**
 * THIS page visit acknowledged the session's payment, so it owes the buyer the confirmation
 * screen — the flow root redirects there instead of letting the onboarding guard bounce them to
 * Studio Home (the acknowledge advances the session to `customization`, which puts it in
 * {@link ONBOARDING_STATUSES} and trips that guard).
 *
 * Narrower than {@link selectSessionCheckedOut} on purpose: an owner who paid on an earlier visit
 * and has since moved into onboarding must keep going to their onboarding.
 */
export const selectShowPaymentConfirmation = (state: RootState): boolean =>
  state.session.paymentAcknowledgedThisVisit;

export const selectIsOnboardingInProgress = (state: RootState): boolean =>
  state.session.status != null && ONBOARDING_STATUSES.has(state.session.status);

export default sessionSlice.reducer;
